import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BarChart3, Loader2, Maximize2, Minimize2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/api";
import { fetchFinanceResultsDashboard, uploadFinanceResultsDashboard } from "@/lib/financeResultsApi";
import { useApp } from "@/store/useApp";
import { hasPermission } from "@/lib/types";
import { useTabQueryRefetch } from "@/layouts/adminTabs/tabRuntime";

const QUERY_KEY = ["finance-results-dashboard"] as const;

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

/**
 * Painel de Resultados (fechamento mensal). O painel é um HTML autocontido gerado a partir de
 * FECHAMENTOS MENSAIS.xlsx e enviado pelo ADMIN; aqui ele é exibido num iframe sandbox (sem acesso à
 * sessão nem ao DOM do sistema). Acesso ADMIN e ADMINISTRATIVO — o backend é a barreira real.
 */
export default function AdminFinanceResults() {
  const account = useApp((s) => s.account);
  const isAdmin = hasPermission(account, "FINANCEIRO.RESULTADOS.VISUALIZAR");
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchFinanceResultsDashboard,
    enabled: isAdmin,
    staleTime: 60_000,
    // Aba inativa (keep-alive) não refaz a consulta no foco da janela/reconexão; ao reativar, as regras
    // normais (staleTime/foco) voltam a valer, sem disparo extra.
    ...useTabQueryRefetch(),
  });

  const upload = useMutation({
    mutationFn: uploadFinanceResultsDashboard,
    onSuccess: () => {
      toast.success("Painel atualizado");
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, "Nao foi possivel enviar o painel")),
  });

  function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) upload.mutate(file);
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="rounded-xl border bg-card p-6 text-sm text-muted-foreground">
          O Painel de Resultados é restrito ao ADMIN e ao Administrativo.
        </div>
      </div>
    );
  }

  return (
    <div className={expanded ? "fixed inset-0 z-50 flex flex-col bg-background" : "flex flex-col gap-4 p-6"}>
      <div className={expanded
        ? "flex items-center justify-between gap-3 border-b px-4 py-2"
        : "rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground"}>
        <div className="flex flex-wrap items-center justify-between gap-3 w-full">
          <div>
            <h1 className={expanded ? "text-base font-semibold" : "text-2xl font-bold"}>Painel de Resultados</h1>
            {!expanded && (
              <p className="mt-1 text-sm text-primary-foreground/80">
                {data
                  ? `Atualizado em ${formatDateTime(data.uploadedAt)} por ${data.uploadedByEmail} · versão ${data.versionCount}`
                  : "Fechamento mensal — faturamento, recebimentos, pagamentos e resultado."}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <input ref={fileInput} type="file" accept=".html,.htm,text/html" className="hidden" onChange={onFileChosen} />
            {data && (
              <Button variant={expanded ? "outline" : "secondary"} size="sm" className="gap-2" onClick={() => setExpanded((v) => !v)}>
                {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                {expanded ? "Sair da tela cheia" : "Tela cheia"}
              </Button>
            )}
            {!expanded && (
              <Button variant="secondary" size="sm" className="gap-2" disabled={upload.isPending}
                onClick={() => fileInput.current?.click()}>
                {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Atualizar painel
              </Button>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando painel…
        </div>
      ) : error ? (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
          {getApiErrorMessage(error, "Nao foi possivel carregar o painel")}
        </div>
      ) : !data?.html ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-10 text-center text-sm text-muted-foreground">
          <BarChart3 className="h-8 w-8" />
          Nenhum painel enviado ainda. Use “Atualizar painel” para enviar o arquivo HTML do fechamento.
        </div>
      ) : (
        <iframe
          title="Painel de Resultados"
          srcDoc={data.html}
          // Sem allow-same-origin: o painel roda isolado, sem acesso a cookies/sessão do sistema.
          sandbox="allow-scripts allow-popups allow-modals allow-downloads"
          className={expanded ? "w-full flex-1 border-0" : "w-full rounded-xl border bg-white"}
          style={expanded ? undefined : { height: "calc(100vh - 230px)", minHeight: 640 }}
        />
      )}
    </div>
  );
}
